import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  DeleteDocumentByID,
  DownloadDocumentByID,
  GetDocumentByAssetID,
  UploadDocumentByAssetID,
} from '../../Services/serviceURL';
import { formatDocumentUploadDate, isUploadFileAllowed } from './assetSummaryDetailsHelpers';

type UseAssetSummaryDocumentsParams = {
  assetId?: string;
  selectedAssetId: string;
  selectedAssetRecordId: string;
  activeTab?: string;
};

export function useAssetSummaryDocuments({
  assetId,
  selectedAssetId,
  selectedAssetRecordId,
  activeTab,
}: UseAssetSummaryDocumentsParams) {
  const [documents, setDocuments] = useState<{ id: number; name: string; date: string }[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [duplicateDocumentInfo, setDuplicateDocumentInfo] = useState<{ id: number; name: string } | null>(null);
  const [pendingUploadData, setPendingUploadData] = useState<{ file: File; title: string } | null>(null);

  const loadDocuments = useCallback(async () => {
    const assetIdToUse = selectedAssetId || assetId;
    if (!assetIdToUse) {
      setDocuments([]);
      setLoadingDocuments(false);
      return;
    }

    setLoadingDocuments(true);
    try {
      const assetData = await GetDocumentByAssetID(assetIdToUse);
      if (!assetData || !Array.isArray(assetData)) {
        setDocuments([]);
        return;
      }

      setDocuments(
        assetData.map((asset: { id: number; documentName: string; uploadedAt?: string }) => ({
          id: asset.id,
          name: asset.documentName,
          date: asset.uploadedAt ? formatDocumentUploadDate(asset.uploadedAt) : '',
        }))
      );
    } catch {
      setDocuments([]);
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocuments(false);
    }
  }, [assetId, selectedAssetId]);

  useEffect(() => {
    if (selectedAssetId || assetId) {
      loadDocuments();
    } else {
      setDocuments([]);
    }
  }, [selectedAssetId, assetId, loadDocuments]);

  useEffect(() => {
    if (activeTab === 'documents') {
      loadDocuments();
    }
  }, [activeTab, loadDocuments]);

  useEffect(() => {
    if (openDropdownId === null) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.document-dropdown-container')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setDocumentTitle('');
    setSelectedFile(null);
    setIsDragging(false);
  };

  const handleConfirmOverwrite = async () => {
    if (!pendingUploadData || !duplicateDocumentInfo) {
      setShowOverwriteModal(false);
      return;
    }

    try {
      const deleteResponse = await DeleteDocumentByID([duplicateDocumentInfo.id.toString()]);
      if (deleteResponse.StatusCode !== 200) {
        toast.error('Failed to delete existing document');
        return;
      }

      const response = await UploadDocumentByAssetID(
        selectedAssetRecordId,
        [pendingUploadData.file],
        pendingUploadData.title
      );
      if (response.StatusCode === 200) {
        toast.success('File uploaded successfully and replaced existing document');
        loadDocuments();
        handleCloseModal();
      } else {
        toast.error(response.Message || 'Failed to upload file');
      }
    } catch {
      toast.error('An error occurred while overwriting the document');
    } finally {
      setShowOverwriteModal(false);
      setDuplicateDocumentInfo(null);
      setPendingUploadData(null);
    }
  };

  const handleCancelOverwrite = () => {
    toast.info('Upload cancelled');
    setShowOverwriteModal(false);
    setDuplicateDocumentInfo(null);
    setPendingUploadData(null);
  };

  const saveDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    if (!isUploadFileAllowed(selectedFile)) {
      toast.error('This file type is not Supported');
      return;
    }
    if (!documentTitle.trim()) {
      toast.error('Please enter a document title');
      return;
    }
    if (!selectedAssetRecordId) {
      toast.error('Please select an asset first');
      return;
    }

    const assetIdToUse = selectedAssetId || assetId;
    if (!assetIdToUse) {
      toast.error('Asset ID is required to check for duplicates');
      return;
    }

    const titleToUse = documentTitle.trim();
    let existingDocuments: Array<{ id?: number; documentName?: string }> = [];
    try {
      const existingDocsData = await GetDocumentByAssetID(assetIdToUse);
      if (Array.isArray(existingDocsData)) existingDocuments = existingDocsData;
    } catch {
      // continue upload
    }

    const duplicateDocument = existingDocuments.find(
      (doc) => doc.documentName?.toLowerCase().trim() === titleToUse.toLowerCase().trim()
    );

    if (duplicateDocument?.id) {
      toast.warning('A document with this name already exists');
      setDuplicateDocumentInfo({ id: duplicateDocument.id, name: duplicateDocument.documentName || titleToUse });
      setPendingUploadData({ file: selectedFile, title: titleToUse });
      setShowOverwriteModal(true);
      return;
    }

    try {
      const response = await UploadDocumentByAssetID(selectedAssetRecordId, [selectedFile], titleToUse);
      if (response.StatusCode === 200) {
        toast.success('File uploaded successfully');
        loadDocuments();
        handleCloseModal();
      } else {
        toast.error(response.Message || 'Failed to upload file');
      }
    } catch {
      toast.error('An error occurred while uploading the document');
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isUploadFileAllowed(file)) {
      toast.error('This file type is not Supported');
      return;
    }
    setSelectedFile(file);
    setDocumentTitle(file.name);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setDocumentTitle(file.name);
  };

  const handleToggleDropdown = (documentId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdownId(openDropdownId === documentId ? null : documentId);
  };

  const handleDeleteFromDropdown = (documentId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdownId(null);
    setDocumentToDelete(documentId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (documentToDelete === null) return;
    const response = await DeleteDocumentByID([documentToDelete.toString()]);
    if (response.StatusCode === 200) {
      loadDocuments();
      toast.success('Document deleted successfully');
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    } else {
      toast.error(response.Message || 'Failed to delete document');
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDocumentToDelete(null);
  };

  const handleDownloadFromDropdown = async (documentId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdownId(null);
    try {
      const response = await DownloadDocumentByID([documentId.toString()]);
      if (response?.StatusCode === 200) {
        toast.success('Document downloaded successfully');
      } else {
        toast.error(response?.Message || 'Failed to download document');
      }
    } catch {
      toast.error('Error while downloading document');
    }
  };

  return {
    documents,
    loadingDocuments,
    showUploadModal,
    setShowUploadModal,
    documentTitle,
    setDocumentTitle,
    selectedFile,
    isDragging,
    openDropdownId,
    showDeleteModal,
    showOverwriteModal,
    duplicateDocumentInfo,
    loadDocuments,
    handleCloseModal,
    handleConfirmOverwrite,
    handleCancelOverwrite,
    saveDocument,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileSelect,
    handleToggleDropdown,
    handleDeleteFromDropdown,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleDownloadFromDropdown,
  };
}
